/** Order document template types (tax invoice + payment receipt). */

export type DocumentTemplateType = "INVOICE" | "RECEIPT";

export const DOCUMENT_TEMPLATE_TYPES: DocumentTemplateType[] = ["INVOICE", "RECEIPT"];

export const DOCUMENT_TEMPLATE_LABELS: Record<DocumentTemplateType, string> = {
  INVOICE: "Tax invoice",
  RECEIPT: "Payment receipt",
};

export type DocumentBlockKind =
  | "logo"
  | "company_info"
  | "document_title"
  | "order_meta"
  | "customer_info"
  | "line_items"
  | "totals"
  | "payment_info"
  | "footer_notes";

export interface DocumentBlockBase {
  id: string;
  enabled?: boolean;
}

export type DocumentBlock =
  | (DocumentBlockBase & { kind: "logo" })
  | (DocumentBlockBase & { kind: "company_info" })
  | (DocumentBlockBase & { kind: "document_title"; title?: string })
  | (DocumentBlockBase & { kind: "order_meta" })
  | (DocumentBlockBase & { kind: "customer_info"; showShipping?: boolean })
  | (DocumentBlockBase & { kind: "line_items" })
  | (DocumentBlockBase & {
      kind: "totals";
      showTax?: boolean;
      showShipping?: boolean;
      showDiscount?: boolean;
    })
  | (DocumentBlockBase & { kind: "payment_info" })
  | (DocumentBlockBase & { kind: "footer_notes"; text?: string });

export const DOCUMENT_BLOCK_LABELS: Record<DocumentBlockKind, string> = {
  logo: "Logo",
  company_info: "Company details",
  document_title: "Document title",
  order_meta: "Order number & date",
  customer_info: "Customer",
  line_items: "Line items",
  totals: "Totals",
  payment_info: "Payment details",
  footer_notes: "Footer notes",
};

function blockId(kind: DocumentBlockKind): string {
  return `${kind}-${Math.random().toString(36).slice(2, 9)}`;
}

export function createDocumentBlock(kind: DocumentBlockKind): DocumentBlock {
  const base = { id: blockId(kind), enabled: true };
  switch (kind) {
    case "document_title":
      return { ...base, kind, title: kind === "document_title" ? "Invoice" : undefined };
    case "customer_info":
      return { ...base, kind, showShipping: true };
    case "totals":
      return { ...base, kind, showTax: true, showShipping: true, showDiscount: true };
    case "footer_notes":
      return { ...base, kind, text: "" };
    default:
      return { ...base, kind } as DocumentBlock;
  }
}

export function defaultInvoiceBlocks(): DocumentBlock[] {
  return [
    { id: blockId("logo"), kind: "logo", enabled: true },
    { id: blockId("company_info"), kind: "company_info", enabled: true },
    { id: blockId("document_title"), kind: "document_title", enabled: true, title: "TAX INVOICE" },
    { id: blockId("order_meta"), kind: "order_meta", enabled: true },
    { id: blockId("customer_info"), kind: "customer_info", enabled: true, showShipping: true },
    { id: blockId("line_items"), kind: "line_items", enabled: true },
    {
      id: blockId("totals"),
      kind: "totals",
      enabled: true,
      showTax: true,
      showShipping: true,
      showDiscount: true,
    },
    { id: blockId("payment_info"), kind: "payment_info", enabled: true },
    { id: blockId("footer_notes"), kind: "footer_notes", enabled: true, text: "" },
  ];
}

export function defaultReceiptBlocks(): DocumentBlock[] {
  return [
    { id: blockId("logo"), kind: "logo", enabled: true },
    { id: blockId("company_info"), kind: "company_info", enabled: true },
    { id: blockId("document_title"), kind: "document_title", enabled: true, title: "RECEIPT" },
    { id: blockId("order_meta"), kind: "order_meta", enabled: true },
    { id: blockId("customer_info"), kind: "customer_info", enabled: true, showShipping: true },
    { id: blockId("line_items"), kind: "line_items", enabled: true },
    {
      id: blockId("totals"),
      kind: "totals",
      enabled: true,
      showTax: true,
      showShipping: true,
      showDiscount: true,
    },
    { id: blockId("payment_info"), kind: "payment_info", enabled: true },
    { id: blockId("footer_notes"), kind: "footer_notes", enabled: true, text: "Thank you for your purchase." },
  ];
}

export function parseDocumentBlocks(raw: unknown): DocumentBlock[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (b): b is DocumentBlock =>
      typeof b === "object" &&
      b !== null &&
      typeof (b as DocumentBlock).id === "string" &&
      typeof (b as DocumentBlock).kind === "string",
  );
}
